/** Tiny synthesised cues via Web Audio: no assets, no network. Context is created on first use (user gesture). */
export const SOUND_THEMES = ['calm', 'energetic', 'funny', 'farts'] as const;
export type SoundTheme = (typeof SOUND_THEMES)[number];

let ctx: AudioContext | null = null;
const ac = (): AudioContext | null => {
  if (typeof window === 'undefined' || !('AudioContext' in window)) return null;
  ctx ??= new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
  return ctx;
};

interface Note {
  freq: number;
  /** start offset, seconds */
  at: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  /** glide to this frequency over the note */
  to?: number;
}

function play(notes: Note[]): void {
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime;
  for (const n of notes) {
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = n.type ?? 'sine';
    o.frequency.setValueAtTime(n.freq, t0 + n.at);
    if (n.to) o.frequency.exponentialRampToValueAtTime(n.to, t0 + n.at + n.dur);
    g.gain.setValueAtTime(0, t0 + n.at);
    g.gain.linearRampToValueAtTime(n.gain ?? 0.08, t0 + n.at + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + n.dur);
    o.connect(g).connect(c.destination);
    o.start(t0 + n.at);
    o.stop(t0 + n.at + n.dur + 0.02);
  }
}

/** Recorded cues (CC0 samples from freesound.org, see README): fetched and decoded once, then reused. */
interface Sample {
  /** one of these is picked at random each time */
  files: string[];
  gain?: number;
}
const decoded = new Map<string, Promise<AudioBuffer | null>>();
const load = (c: AudioContext, url: string): Promise<AudioBuffer | null> => {
  let p = decoded.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => r.arrayBuffer())
      .then((b) => c.decodeAudioData(b))
      .catch(() => null);
    decoded.set(url, p);
  }
  return p;
};
function playSample(s: Sample): void {
  const c = ac();
  if (!c) return;
  const url = s.files[Math.floor(Math.random() * s.files.length)] as string;
  void load(c, url).then((buf) => {
    if (!buf) return;
    const src = c.createBufferSource();
    src.buffer = buf;
    const g = c.createGain();
    g.gain.value = s.gain ?? 0.6;
    src.connect(g).connect(c.destination);
    src.start();
  });
}
/** Warm the cache so the first cue of a theme is not late. */
export function preload(theme: SoundTheme): void {
  const c = ac();
  if (!c) return;
  for (const k of ['correct', 'wrong', 'tap'] as const) {
    const x = THEMES[theme][k];
    if (!Array.isArray(x)) for (const f of x.files) void load(c, f);
  }
}

type Cue = Note[] | Sample;
const cue = (x: Cue): void => (Array.isArray(x) ? play(x) : playSample(x));

/** Cue sets, per client request: calm / energetic / funny / farts. */
const THEMES: Record<SoundTheme, { correct: Cue; wrong: Cue; tap: Cue }> = {
  calm: {
    correct: [{ freq: 523, at: 0, dur: 0.18, gain: 0.05 }, { freq: 784, at: 0.12, dur: 0.3, gain: 0.05 }],
    wrong: [{ freq: 220, at: 0, dur: 0.35, type: 'triangle', gain: 0.05 }],
    tap: [{ freq: 440, at: 0, dur: 0.05, type: 'triangle', gain: 0.03 }],
  },
  energetic: {
    correct: [
      { freq: 660, at: 0, dur: 0.06, type: 'square', gain: 0.05 },
      { freq: 880, at: 0.06, dur: 0.06, type: 'square', gain: 0.05 },
      { freq: 1320, at: 0.12, dur: 0.14, type: 'square', gain: 0.05 },
    ],
    wrong: [{ freq: 200, at: 0, dur: 0.12, type: 'sawtooth', gain: 0.06 }, { freq: 150, at: 0.12, dur: 0.16, type: 'sawtooth', gain: 0.06 }],
    tap: [{ freq: 500, at: 0, dur: 0.04, type: 'square', gain: 0.03 }],
  },
  funny: {
    // slide-whistle up for correct, "wah-wah" down for wrong, boing for tap
    correct: [{ freq: 400, at: 0, dur: 0.3, to: 1200, gain: 0.07 }],
    wrong: [{ freq: 420, at: 0, dur: 0.18, to: 300, type: 'triangle', gain: 0.07 }, { freq: 300, at: 0.2, dur: 0.3, to: 180, type: 'triangle', gain: 0.07 }],
    tap: [{ freq: 300, at: 0, dur: 0.12, to: 600, type: 'triangle', gain: 0.04 }],
  },
  farts: {
    correct: { files: ['/sfx/fart-ok-1.mp3', '/sfx/fart-ok-2.mp3'] },
    wrong: { files: ['/sfx/fart-bad-1.mp3', '/sfx/fart-bad-2.mp3'] },
    tap: { files: ['/sfx/fart-tap.mp3'], gain: 0.4 },
  },
};

export const sfx = {
  correct: (theme: SoundTheme = 'calm'): void => cue(THEMES[theme].correct),
  wrong: (theme: SoundTheme = 'calm'): void => cue(THEMES[theme].wrong),
  tap: (theme: SoundTheme = 'calm'): void => cue(THEMES[theme].tap),
};
